<template>
  <div class="invoices-container">
    <!-- Header Section -->
    <div class="header-card">
      <v-row>
        <v-col :cols="isMobile ? 12 : 4">
          <h4 class="page-title" :style="{ color: getOutlineColor }">
            <v-icon large :color="getOutlineColor" class="mr-2"
              >mdi-file-document-multiple</v-icon
            >
            {{ itemLabel }}s
          </h4></v-col
        >
        <v-col :cols="isMobile ? 12 : 4">
          <!-- Search Bar -->
          <v-text-field
            v-model="searchInvoices"
            outlined
            dense
            :placeholder="`Search ${itemLabel}s...`"
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="search-field"
            clearable
          ></v-text-field>
        </v-col>

        <!-- Action Buttons -->
        <v-col :cols="isMobile ? 12 : 4">
          <div class="action-buttons">
            <v-btn
              :color="getThemeColor"
              dark
              v-if="table === 'invoices'"
              elevation="2"
              @click="openMarkPaidDialog()"
              small
              class="action-btn"
            >
              <v-icon left small>mdi-check-circle</v-icon>
              Mark Paid
            </v-btn>

            <v-btn
              color="error"
              dark
              elevation="2"
              @click="deleteSelectedInvoices()"
              small
              class="action-btn"
            >
              <v-icon left small>mdi-delete</v-icon>
              Delete
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Mobile View -->
    <div class="mobile-view d-md-none py-1" v-if="isMobile">
      <!-- Mobile Card List -->
      <v-card
        v-for="invoice in filteredInvoices"
        :key="invoice.id"
        elevation="2"
        outlined
        class="invoice-card mb-3 rounded-xl overflow-hidden bg-white"
      >
        <!-- Card Header: Selection, Ref # & Status -->
        <div class="px-4 pt-3 pb-2 d-flex align-center bg-light-header">
          <v-checkbox
            @change="getSelectedInvoice($event, invoice)"
            class="mt-0 pt-0 mr-2"
            hide-details
            color="primary"
            dense
          ></v-checkbox>

          <span
            class="text-subtitle-1 font-weight-bold grey--text text--darken-4 text-truncate"
          >
            {{ invoice[referenceNumber] }}
          </span>

          <v-spacer></v-spacer>

          <v-chip
            x-small
            label
            :color="determineColor(invoice)"
            dark
            class="font-weight-bold rounded-lg px-2 text-capitalize"
          >
            {{ getInvoiceStatus(invoice) }}
          </v-chip>
        </div>

        <v-divider></v-divider>

        <!-- Card Body: Details Grid -->
        <v-card-text class="pa-4" v-if="invoice.calculation">
          <!-- Client & Case Title -->
          <div class="d-flex align-start mb-2" v-if="invoice.client">
            <v-icon small color="grey darken-1" class="mr-2 mt-1"
              >mdi-account-outline</v-icon
            >
            <div class="text-body-2 line-height-tight">
              <span class="font-weight-medium grey--text text--darken-3">
                {{ invoice.client.name }}
              </span>
              <span
                v-if="invoice.case"
                class="grey--text text--darken-1 text-caption d-block"
              >
                [{{ invoice.case.title }}]
              </span>
            </div>
          </div>

          <!-- Issue Date -->
          <div class="d-flex align-center mb-3">
            <v-icon small color="grey darken-1" class="mr-2"
              >mdi-calendar-blank-outline</v-icon
            >
            <span class="text-caption grey--text text--darken-1 mr-2"
              >Date:</span
            >
            <span
              class="text-body-2 font-weight-medium grey--text text--darken-3"
            >
              {{ invoice.invoice_date }}
            </span>
          </div>

          <!-- Financial Breakdown Container -->
          <div class="pa-3 rounded-lg grey lighten-5 border-grey">
            <!-- Total -->
            <div class="d-flex justify-space-between align-center mb-1">
              <span
                class="text-caption grey--text text--darken-1 font-weight-medium"
                >Total Amount</span
              >
              <span
                class="text-subtitle-2 font-weight-bold grey--text text--darken-4"
              >
                {{ getCurrency
                }}{{ formatNumber(invoice.calculation?.total || 0) }}
              </span>
            </div>

            <!-- Paid -->
            <div class="d-flex justify-space-between align-center mb-1">
              <span
                class="text-caption grey--text text--darken-1 font-weight-medium"
                >Amount Paid</span
              >
              <span
                class="text-subtitle-2 font-weight-medium green--text text--darken-2"
              >
                {{ getCurrency }}{{ formatNumber(getTotalPayment(invoice)) }}
              </span>
            </div>

            <!-- Balance Due (If unpaid/partially paid) -->
            <div
              class="d-flex justify-space-between align-center pt-2 border-top-dashed"
              v-if="
                getTotalPayment(invoice) <
                Number(invoice.calculation?.total || 0)
              "
            >
              <span
                class="text-caption font-weight-bold red--text text--darken-2 d-flex align-center"
              >
                <v-icon x-small color="red darken-2" class="mr-1"
                  >mdi-alert-circle-outline</v-icon
                >
                Balance Due
              </span>
              <span
                class="text-subtitle-1 font-weight-black red--text text--darken-2"
              >
                {{ getCurrency
                }}{{
                  formatNumber(
                    Number(invoice.calculation?.total || 0) -
                      getTotalPayment(invoice)
                  )
                }}
              </span>
            </div>
          </div>

          <!-- Due Date Banner (If unpaid) -->
          <div
            class="d-flex align-center mt-2 px-2 py-1 rounded bg-red-lighten"
            v-if="
              getTotalPayment(invoice) <
                Number(invoice.calculation?.total || 0) && invoice.due_date
            "
          >
            <v-icon x-small color="red darken-2" class="mr-1"
              >mdi-clock-outline</v-icon
            >
            <span
              class="text-caption red--text text--darken-3 font-weight-medium"
            >
              Due by {{ invoice.due_date }}
            </span>
          </div>
        </v-card-text>

        <!-- Mobile Action Bar -->
        <v-card-actions class="px-4 pb-3 pt-0">
          <v-btn
            depressed
            rounded
            small
            :color="getThemeColor"
            dark
            class="px-4 text-capitalize font-weight-bold text-caption"
            @click="editSelectedInvoice(invoice)"
          >
            <v-icon x-small left>mdi-pencil-outline</v-icon>
            Edit
          </v-btn>

          <v-spacer></v-spacer>

          <ProFormaGenerator
            :invoice="invoice"
            :title="itemLabel.includes('nvoice') ? 'TAX INVOICE' : 'QUOTATION'"
          ></ProFormaGenerator>
        </v-card-actions>
      </v-card>

      <!-- Native Empty State -->
      <div v-if="filteredInvoices.length === 0" class="text-center py-12 px-4">
        <v-avatar size="80" color="grey lighten-4" class="mb-3">
          <v-icon size="40" color="grey lighten-1"
            >mdi-file-document-outline</v-icon
          >
        </v-avatar>
        <div class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
          No {{ itemLabel }}s found
        </div>
        <div class="text-caption grey--text">
          Try adjusting your filters or search criteria.
        </div>
      </div>
    </div>

    <!-- Desktop View -->
    <v-card class="desktop-view d-none d-md-block" elevation="2" v-else>
      <v-data-table
        :headers="invoiceHeaders"
        :items="invoices"
        :items-per-page="10"
        :search="searchInvoices"
        class="modern-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <!-- Master Checkbox in Header -->
        <!-- <template v-slot:header.selection="{ header }">
          <v-checkbox
            v-model="masterSelected"
            :indeterminate="isIndeterminate"
            @change="toggleSelectAll"
            hide-details
            class="ma-0 pa-0"
          ></v-checkbox>
        </template> -->
        <template v-slot:item.selection="{ item }">
          <div class="action-cell">
            <v-checkbox
              @change="getSelectedInvoice($event, item)"
              hide-details
              class="ma-0"
            ></v-checkbox>
            <!-- <v-btn
               :value="item"
              v-model="selectedInvoicesForPayment"
              icon
              small
              :color="getOutlineColor"
              @click="editSelectedInvoice(item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn> -->
            <ProFormaGenerator
              :invoice="item"
              :title="
                itemLabel.includes('nvoice') ? 'TAX INVOICE' : 'QUOTATION'
              "
            ></ProFormaGenerator>
            <v-icon
              dark
              color="warning"
              v-if="table === 'quotations'"
              @click="viewItem(item)"
              >mdi-eye</v-icon
            >
          </div>
        </template>

        <template v-slot:item[referenceNumber]="{ item }">
          <span class="font-weight-bold">{{ item[referenceNumber] }}</span>
        </template>

        <template v-slot:item.paid="{ item }">
          <v-chip
            small
            :color="getTotalPayment(item) === 0 ? 'grey' : 'success'"
            dark
          >
            <v-icon left x-small>
              {{ getTotalPayment(item) === 0 ? "mdi-close" : "mdi-check" }}
            </v-icon>
            {{
              getTotalPayment(item) === 0
                ? "Unpaid"
                : getCurrency + formatNumber(getTotalPayment(item))
            }}
          </v-chip>
        </template>

        <template v-slot:item.due_date="{ item }">
          <v-chip
            v-if="
              item.calculation &&
              getTotalPayment(item) >= Number(item.calculation?.total || 0)
            "
            small
            color="success"
            dark
          >
            <v-icon left x-small>mdi-check-circle</v-icon>
            Completed
          </v-chip>
          <v-chip
            v-else
            small
            :color="isOverdue(item.due_date) ? 'error' : 'warning'"
            dark
          >
            <v-icon left x-small>mdi-calendar-clock</v-icon>
            {{ item.due_date }}
          </v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="determineColor(item)" dark>
            {{ getStatusText(item) }}
          </v-chip>
        </template>

        <template v-slot:item.calculation.total="{ item }">
          <strong dark>
            {{ getCurrency }}{{ formatMoney(item.calculation?.total || 0) }}
          </strong>
        </template>
      </v-data-table>
    </v-card>

    <!-- Mark Paid Dialog -->
    <v-dialog v-model="markPaidDialog" max-width="900" persistent scrollable>
      <v-card class="mark-paid-card">
        <v-card-title class="dialog-title">
          <v-icon left :color="getThemeColor">mdi-check-circle</v-icon>
          Mark {{ itemLabel }}s as Paid
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4" v-if="selectedInvoicesForPayment">
          <v-alert type="info" text dense class="mb-4">
            {{ selectedInvoicesForPayment.length }}
            {{
              selectedInvoicesForPayment.length === 1 ? "invoice" : "invoices"
            }}
            selected for payment
          </v-alert>

          <!-- Global Payment Settings -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="font-weight-bold">
              Payment Details (Applied to all selected invoices)
            </v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="4">
                  <DatePickerWithMenu
                    label="Payment Date"
                    :default="dateInvoicesPaid"
                    @date-selected="getSelectedPaymentDate"
                  ></DatePickerWithMenu>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    label="Payment Description"
                    v-model="paymentDescription"
                  >
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="paymentMethod"
                    :items="paymentMethods"
                    label="Payment Method"
                    outlined
                    dense
                    prepend-inner-icon="mdi-credit-card"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Individual Invoice Payments -->
          <div
            v-for="(invoice, ind) in selectedInvoicesForPayment"
            :key="invoice?.id"
          >
            <v-card outlined class="invoice-payment-card mb-3">
              <v-card-text
                v-if="
                  invoice && invoice.calculation && invoice.calculation.total
                "
              >
                <v-row align="center">
                  <v-col cols="12" sm="1" class="text-center">
                    <v-avatar :color="getThemeColor" size="32">
                      <span class="white--text">{{ ind + 1 }}</span>
                    </v-avatar>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <div class="invoice-info" v-if="invoice.client">
                      <div class="font-weight-bold">
                        {{ invoice.invoice_number }}
                      </div>
                      <div class="text-caption">{{ invoice.client.name }}</div>
                      <div class="text-caption grey--text">
                        {{ invoice.client.institution }}
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="3">
                    <div class="payment-summary">
                      <div class="text-caption">Total Amount</div>
                      <div class="font-weight-bold">
                        {{ getCurrency
                        }}{{ formatNumber(invoice.calculation?.total || 0) }}
                      </div>
                      <div class="text-caption">
                        <!-- UPDATED: Replaced invoice.totalPayment with getTotalPayment(invoice) -->
                        Paid: {{ getCurrency
                        }}{{ formatNumber(getTotalPayment(invoice)) }}
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      :label="'Payment Amount (' + getCurrency + ')'"
                      outlined
                      dense
                      type="number"
                      :value="getPaymentAmount(invoice, ind)"
                      @input="updatePaymentAmount(ind, $event)"
                      prepend-inner-icon="mdi-cash-multiple"
                      :hint="
                        'Balance: ' +
                        getCurrency +
                        formatNumber(
                          Number(invoice.calculation?.total || 0) -
                            getTotalPayment(invoice)
                        )
                      "
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Quick Payment Buttons -->
                <v-row class="mt-2">
                  <v-col>
                    <div class="quick-payment-btns">
                      <!-- UPDATED: Math calculation references updated to function patterns -->
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click="
                          setPaymentAmount(
                            ind,
                            Number(invoice.calculation?.total || 0) -
                              getTotalPayment(invoice)
                          )
                        "
                      >
                        Full Balance
                      </v-btn>
                      <!-- UPDATED: Math calculation references updated to function patterns -->
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click="
                          setPaymentAmount(
                            ind,
                            (Number(invoice.calculation?.total || 0) -
                              getTotalPayment(invoice)) /
                              2
                          )
                        "
                      >
                        50%
                      </v-btn>
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click="setPaymentAmount(ind, 0)"
                      >
                        Clear
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <!-- Payment Summary -->
          <v-card outlined color="grey lighten-4">
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="summary-item">
                    <span class="summary-label">Total Payment:</span>
                    <span class="summary-value">
                      {{ getCurrency
                      }}{{ formatNumber(calculateTotalPayment()) }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="summary-item">
                    <span class="summary-label">Remaining Balance:</span>
                    <span class="summary-value">
                      {{ getCurrency }}{{ formatNumber(remainingBalance) }}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn outlined @click="closeDialog"> Cancel </v-btn>
          <v-btn
            :color="getThemeColor"
            dark
            large
            @click="markInvoicePaidNow()"
            :loading="markingPaid"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Confirm Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <GlobalConfirmDeleteDialog
      :item="confirmDeleteTitle"
      @delete-confirmed="takeActionAfterInvoiceDeleteChoice"
    ></GlobalConfirmDeleteDialog>

    <!-- Loading Overlay -->
    <v-overlay :value="sendingReminders" z-index="999">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
      <div class="mt-4 white--text">Sending reminders...</div>
    </v-overlay>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers";
import pdfMakeParts from "@/plugins/pdfMakeParts";
import DatePickerWithMenu from "@/components/MyLibrary/DatePickerWithMenu.vue";
import GlobalConfirmDeleteDialog from "@/components/MyLibrary/GlobalConfirmDeleteDialog.vue";
import ProFormaGenerator from "@/modules/CaseManager/ProFormaGenerator.vue";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";

// Register the fonts automatically
pdfMake.addVirtualFileSystem(pdfFonts);

export default {
  name: "InvoiceTable",
  props: {
    invoices: {
      type: Array,
      default: () => [],
    },
    table: {
      type: String,
      default: "invoices",
    },
    itemLabel: {
      type: String,
      default: "Invoice",
    },
    referenceNumber: {
      type: String,
      default: "invoice_number",
    },
  },

  components: {
    DatePickerWithMenu,
    GlobalConfirmDeleteDialog,
    ProFormaGenerator,
  },
  data() {
    return {
      searchInvoices: "",
      selectedInvoicesForPayment: [],
      selectedInvoice: null,
      selectedInvoices: [],
      markPaidDialog: false,
      dateInvoicesPaid: new Date().toISOString().substr(0, 10),
      paymentDescription: "Invoice Payment",
      paymentMethod: "EFT",
      remainingBalance: 0,
      paymentMethods: [
        "ATM Deposit",
        "Cash",
        "Checks",
        "Credit Cards",
        "Debit Cards",
        "EFT",
        "Mobile Payments",
      ],
      confirmDeleteTitle: null,
      markingPaid: false,
      sendingReminders: false,
    };
  },
  mounted() {
    this.initialiseComponent();
  },
  computed: {
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getTrustAccLabel() {
      return this.$store.getters.getTrustAccLabel;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    gradientIconStyle() {
      return this.$store.getters.gradientIconStyle;
    },
    getOutlineColor() {
      return this.$store.getters.getOutlineColor;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    invoiceHeaders() {
      const headers = [
        { text: "Actions", value: "selection", sortable: false },
      ];

      if (this.table === "quotations") {
        headers.push({ text: "Client", value: "client.name" });
        headers.push({
          text: `${this.itemLabel} #`,
          value: this.referenceNumber,
        });
        headers.push({ text: "Total", value: "calculation.total" });
        headers.push({ text: "Date", value: "quote_date" });
      } else {
        headers.push({
          text: `${this.itemLabel} #`,
          value: this.referenceNumber,
        });
        headers.push({ text: "Date", value: "invoice_date" });
        headers.push({ text: "Total", value: "calculation.total" });
        headers.push({ text: "Payment", value: "paid" });
        headers.push({ text: "Status", value: "status" });
      }

      return headers;
    },
    isIndeterminate() {
      const selectedCount = this.selectedInvoicesForPayment.length;
      return selectedCount > 0 && selectedCount < this.filteredInvoices.length;
    },
    filteredInvoices() {
      if (!this.searchInvoices) {
        return this.invoices;
      }
      const search = this.searchInvoices.toLowerCase();
      return this.invoices.filter((invoice) => {
        return (
          (invoice[this.referenceNumber] &&
            invoice[this.referenceNumber].toLowerCase().includes(search)) ||
          (invoice.client &&
            invoice.client.name &&
            invoice.client.name.toLowerCase().includes(search)) ||
          (invoice.case &&
            invoice.case.title &&
            invoice.case.title.toLowerCase().includes(search))
        );
      });
    },
  },
  watch: {
    selectedInvoicesForPayment(newVal) {
      if (!newVal || newVal.length < 1) {
        return;
      }

      newVal.forEach((invoice) => {
        if (!invoice?.newPayment) {
          const totalPayment = this.getTotalPayment(invoice);
          this.$set(
            invoice,
            "newPayment",
            Number(Number(invoice.calculation?.total || 0) - totalPayment)
          );
        }
      });
      this.calculateRemainingBalance();
    },
  },
  methods: {
    initialiseComponent() {
      // Clean initialization matching backend expected strings
      const paym = this.getTrustAccLabel + " Account";
      if (this.getTrustAccLabel.includes("rust")) {
        this.paymentMethod = paym;
      } else {
        this.paymentMethod = "EFT";
      }
      if (!this.paymentMethods.includes(paym)) {
        this.paymentMethods.unshift(paym);
      }
    },
    getSelectedInvoice(checked, item) {
      const index = this.selectedInvoicesForPayment.findIndex((invoice) => {
        return invoice.id === item.id;
      });

      const notExist = index === -1;
      const exist = index !== -1;

      if (!checked && exist) {
        this.selectedInvoicesForPayment.splice(index, 1);
        this.selectedInvoices = [...this.selectedInvoicesForPayment];
        return;
      }

      if (checked && exist) {
        this.selectedInvoices = [...this.selectedInvoicesForPayment];
        return;
      }

      if (checked && notExist) {
        this.selectedInvoicesForPayment.push(item);
        this.selectedInvoices = [...this.selectedInvoicesForPayment];
        return;
      }
    },
    toggleSelectAll(value) {
      if (value) {
        this.selectedInvoicesForPayment = [];
        this.invoices.forEach((invoice) => {
          this.selectedInvoicesForPayment.push(invoice);
        });
        this.selectedInvoices = [...this.selectedInvoicesForPayment];
        // console.log(this.selectedInvoicesForPayment);
        // console.log(this.selectedInvoices);
      } else {
        // console.log("else");
        this.selectedInvoicesForPayment = [];
        this.selectedInvoices = [];
      }
    },
    formatMoney(amount) {
      return helpersJs.formatMoney(amount);
    },

    viewItem(item) {
      this.$emit("get-item", item);
    },

    getTotalPayment(invoice) {
      if (!invoice || !invoice.payments || !invoice.payments.length) {
        return 0;
      }
      return invoice.payments.reduce(
        (acc, payment) =>
          acc + Number(payment.amount_paid || payment.amount || 0),
        0
      );
    },

    formatNumber(value) {
      if (!value) return "0.00";
      return Number(value).toFixed(2);
    },

    determineNewPayment() {
      if (!this.invoices || this.invoices.length === 0) {
        return;
      }

      this.invoices.forEach((invoice) => {
        if (
          invoice &&
          invoice.calculation &&
          invoice.calculation.total !== undefined &&
          invoice.calculation.total !== null
        ) {
          if (invoice.newPayment === undefined || invoice.newPayment === null) {
            const totalPayment = this.getTotalPayment(invoice);
            this.$set(
              invoice,
              "newPayment",
              Number(Number(invoice.calculation?.total || 0) - totalPayment)
            );
          }
        }
      });
    },

    openMarkPaidDialog() {
      if (
        !this.selectedInvoicesForPayment ||
        this.selectedInvoicesForPayment.length < 1
      ) {
        this.$toast.info("Please select at least one invoice", {
          duration: 10000,
        });
        this.closeDialog();
        return;
      }
      this.determineNewPayment();
      this.calculateRemainingBalance();
      this.markPaidDialog = true;
    },

    getPaymentAmount(invoice) {
      if (!invoice) return 0;
      if (invoice.newPayment !== undefined && invoice.newPayment !== null) {
        return invoice.newPayment;
      }
      return Number(
        Number(invoice.calculation?.total || 0) - this.getTotalPayment(invoice)
      );
    },

    updatePaymentAmount(ind, value) {
      if (this.selectedInvoicesForPayment[ind]) {
        this.$set(
          this.selectedInvoicesForPayment[ind],
          "newPayment",
          Number(value)
        );
        this.calculateRemainingBalance();
      }
    },

    setPaymentAmount(ind, amount) {
      if (this.selectedInvoicesForPayment[ind]) {
        this.$set(
          this.selectedInvoicesForPayment[ind],
          "newPayment",
          Number(amount)
        );
        this.calculateRemainingBalance();
      }
    },

    calculateTotalPayment() {
      if (!Array.isArray(this.selectedInvoicesForPayment)) return 0;
      return this.selectedInvoicesForPayment.reduce((sum, invoice) => {
        if (!invoice) return sum;
        const payment =
          invoice.newPayment !== undefined && invoice.newPayment !== null
            ? invoice.newPayment
            : Number(invoice.calculation?.total || 0) -
              this.getTotalPayment(invoice);
        return sum + Number(payment);
      }, 0);
    },

    calculateRemainingBalance() {
      if (!Array.isArray(this.selectedInvoicesForPayment)) {
        this.remainingBalance = 0;
        return;
      }
      this.remainingBalance = this.selectedInvoicesForPayment.reduce(
        (sum, invoice) => {
          if (!invoice) return sum;
          const payment =
            invoice.newPayment !== undefined && invoice.newPayment !== null
              ? invoice.newPayment
              : Number(invoice.calculation?.total || 0) -
                this.getTotalPayment(invoice);

          const remaining =
            Number(invoice.calculation?.total || 0) -
            this.getTotalPayment(invoice) -
            Number(payment);
          return sum + remaining;
        },
        0
      );
    },

    getSelectedPaymentDate(dateInvoicesPaid) {
      this.dateInvoicesPaid = dateInvoicesPaid;
    },

    editSelectedInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.$emit("edit-invoice", invoice);
    },

    markInvoicePaidNow() {
      if (!this.getUserCompany || this.getUserCompany.package === "freemium") {
        this.$store.commit("setUpgradeDialog", true);
        this.closeDialog();
        return;
      }

      this.markingPaid = true;
      this.$store.commit("setLoader", true);

      // Construct a standardized payload matching backend property expectations
      const payloadInvoices = this.selectedInvoicesForPayment.map((inv) => ({
        id: inv.id,
        invoice_number: inv.invoice_number || inv.invoice_no,
        client_reference: inv.client_reference || inv.client?.clientRef || null,
        case_id: inv.case_id || inv.invoice_number || inv.invoice_no || null,
        calculation: inv.calculation,
        payments: inv.payments || inv.all_payments || inv.allPayments || [],
        newPayment:
          inv.newPayment !== undefined
            ? inv.newPayment
            : Number(inv.calculation?.total || 0) - this.getTotalPayment(inv),
      }));

      axios
        .postDataWithToken(
          "/auth/mark-invoices-paid",
          {
            selectedInvoicesForPayment: payloadInvoices,
            dateInvoicesPaid: this.dateInvoicesPaid,
            paymentMethod: this.paymentMethod,
            paymentDescription: this.paymentDescription,
          },
          this.getAccessToken
        )
        .then((response) => {
          this.selectedInvoicesForPayment = [];
          this.$emit("refresh-needed");
          if (response) {
            if (response.status === "success") {
              this.$toast.success(response.message, { duration: 10000 });
            } else if (response.status === "partial") {
              this.$toast.warning(response.message, { duration: 10000 });
            }
          }
        })
        .catch((error) => {
          this.$toast.error("Failed to mark all invoices as paid!", {
            duration: 10000,
          });
        })
        .finally(() => {
          this.closeDialog();
          this.$store.commit("setLoader", false);
        });
    },

    determineColor(invoice) {
      if (!invoice || !invoice.calculation) return "grey";

      if (
        Number(invoice.calculation?.total || 0) ===
        this.getTotalPayment(invoice)
      ) {
        return "#006064";
      } else if (this.isOverdue(invoice.due_date)) {
        return "error";
      } else {
        return "#01579B";
      }
    },

    isOverdue(dueDate) {
      const today = new Date().toISOString().substr(0, 10);
      return dueDate < today;
    },

    getInvoiceStatus(invoice) {
      if (!invoice) return;
      if (
        this.getTotalPayment(invoice) >= Number(invoice.calculation?.total || 0)
      ) {
        return "Paid";
      } else if (this.isOverdue(invoice.due_date)) {
        return "Overdue";
      } else {
        return "Pending";
      }
    },

    getStatusText(invoice) {
      if (
        this.getTotalPayment(invoice) >= Number(invoice.calculation?.total || 0)
      ) {
        return "Paid";
      } else {
        const due =
          Number(invoice.calculation?.total || 0) -
          this.getTotalPayment(invoice);
        return `Due ${this.getCurrency}${this.formatNumber(due)}`;
      }
    },

    deleteSelectedInvoices() {
      if (this.selectedInvoicesForPayment.length < 1) {
        this.$toast.error(`Please select at least one ${this.itemLabel}`, {
          duration: 10000,
        });
        return;
      }
      this.$store.commit("setGlobalConfirmDelete", true);
      this.confirmDeleteTitle = `Selected ${this.itemLabel}s`;
    },

    takeActionAfterInvoiceDeleteChoice(choice) {
      if (choice) {
        if (this.table) {
          this.selectedInvoices = [];
          this.selectedInvoicesForPayment.forEach((invoice) => {
            this.selectedInvoices.push(invoice.id);
          });
          this.bulkDeleteItems(this.selectedInvoices);
        } else {
          this.selectedInvoicesForPayment = [];
        }
      } else {
        this.$store.commit("setGlobalConfirmDelete", false);
      }
    },

    bulkDeleteItems(rowIds) {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/bulk-delete",
          { table: this.table, rowIds },
          this.getAccessToken
        )
        .then((response) => {
          this.$emit("refresh-needed");
          this.$store.commit("setSnackbar", {
            show: true,
            message: "Invoices deleted successfully",
            color: "success",
          });
        })
        .catch((error) => {
          this.$store.commit("setSnackbar", {
            show: true,
            message: "Failed to delete invoices",
            color: "error",
          });
        })
        .finally(() => {
          this.$store.commit("setLoader", false);
          this.closeDialog();
        });
    },

    closeDialog() {
      this.markPaidDialog = false;
      this.confirmDeleteTitle = null;
      this.markingPaid = false;
      // Clear local selection arrays AND notify parent components to prevent auto-selecting behavior
      this.selectedInvoicesForPayment = [];
      this.selectedInvoices = [];
      this.$emit("update:selected", []);
    },

    async printPaymentReceipt(invoice) {
      if (!invoice || !invoice.calculation) return;

      const totalPaid = this.getTotalPayment(invoice);
      const balance = (invoice.calculation?.total || 0) - totalPaid;
      const isPaidInFull = balance <= 0;

      const docDefinition = {
        pageSize: "A4",
        pageMargins: [40, 60, 40, 60],
        content: [
          {
            text: "PAYMENT RECEIPT",
            style: "header",
            alignment: "center",
            margin: [0, 0, 0, 10],
          },
          {
            columns: [
              {
                width: "*",
                text: [
                  { text: "Receipt #: ", bold: true },
                  { text: `RCP-${invoice.invoice_number}` },
                ],
              },
              {
                width: "*",
                text: [
                  { text: "Date: ", bold: true },
                  { text: new Date().toLocaleDateString() },
                ],
                alignment: "right",
              },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            text: "CLIENT INFORMATION",
            style: "subheader",
            margin: [0, 0, 0, 10],
          },
          {
            columns: [
              {
                width: "50%",
                stack: [
                  {
                    text: [{ text: "Name: ", bold: true }, invoice.client.name],
                  },
                  invoice.client.institution
                    ? {
                        text: [
                          { text: "Institution: ", bold: true },
                          invoice.client.institution,
                        ],
                        margin: [0, 5, 0, 0],
                      }
                    : {},
                  invoice.client.email
                    ? {
                        text: [
                          { text: "Email: ", bold: true },
                          invoice.client.email,
                        ],
                        margin: [0, 5, 0, 0],
                      }
                    : {},
                ],
              },
              {
                width: "50%",
                stack: [
                  invoice.client.phone
                    ? {
                        text: [
                          { text: "Phone: ", bold: true },
                          invoice.client.phone,
                        ],
                      }
                    : {},
                  invoice.case
                    ? {
                        text: [
                          { text: "Case: ", bold: true },
                          invoice.case.title,
                        ],
                        margin: [0, 5, 0, 0],
                      }
                    : {},
                ],
              },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            text: "INVOICE DETAILS",
            style: "subheader",
            margin: [0, 0, 0, 10],
          },
          {
            table: {
              widths: ["*", "*"],
              body: [
                [
                  { text: "Invoice Number", bold: true, fillColor: "#f5f5f5" },
                  invoice.invoice_number,
                ],
                [
                  { text: "Invoice Date", bold: true, fillColor: "#f5f5f5" },
                  invoice.invoice_date,
                ],
                [
                  { text: "Due Date", bold: true, fillColor: "#f5f5f5" },
                  invoice.due_date,
                ],
              ],
            },
            margin: [0, 0, 0, 10],
          },
          {
            text: "PAYMENT INFORMATION",
            style: "subheader",
            margin: [0, 0, 0, 10],
          },
          {
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  { text: "Invoice Total", bold: true },
                  {
                    text: `${this.getCurrency}${this.formatNumber(
                      invoice.calculation?.total || 0
                    )}`,
                    alignment: "right",
                  },
                ],
                [
                  { text: "Total Paid", bold: true, fillColor: "#e8f5e9" },
                  {
                    text: `${this.getCurrency}${this.formatNumber(totalPaid)}`,
                    alignment: "right",
                    fillColor: "#e8f5e9",
                  },
                ],
                [
                  {
                    text: "Balance Remaining",
                    bold: true,
                    fillColor: balance > 0 ? "#fff3e0" : "#e8f5e9",
                  },
                  {
                    text: `${this.getCurrency}${this.formatNumber(
                      Math.max(0, balance)
                    )}`,
                    alignment: "right",
                    fillColor: balance > 0 ? "#fff3e0" : "#e8f5e9",
                    bold: true,
                  },
                ],
              ],
            },
            margin: [0, 0, 0, 10],
          },
          {
            text: isPaidInFull
              ? "STATUS: PAID IN FULL"
              : "STATUS: PARTIAL PAYMENT",
            style: "status",
            alignment: "center",
            color: isPaidInFull ? "#2e7d32" : "#f57c00",
            margin: [0, 20, 0, 20],
          },
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            text: "Thank you for your payment!",
            style: "footer",
            alignment: "center",
          },
          {
            text: "This is a computer-generated receipt and does not require a signature.",
            style: "smallFooter",
            alignment: "center",
            margin: [0, 10, 0, 0],
          },
        ],
        styles: {
          header: {
            fontSize: 24,
            bold: true,
            color: "#1976d2",
          },
          subheader: {
            fontSize: 14,
            bold: true,
            color: "#424242",
          },
          status: {
            fontSize: 16,
            bold: true,
          },
          footer: {
            fontSize: 12,
            color: "#757575",
          },
          smallFooter: {
            fontSize: 9,
            color: "#9e9e9e",
            italics: true,
          },
        },
      };

      docDefinition.content.unshift(
        await pdfMakeParts.companyHeader("Receipt")
      );

      pdfMake
        .createPdf(docDefinition)
        .download(`payment-receipt-${invoice.invoice_number}.pdf`);
    },
  },
};
</script>

<style scoped>
.invoices-container {
  padding: 16px;
  background: #f5f5f5;
  min-height: 100vh;
}

.header-card {
  background: white;
  border-radius: 12px;
  padding: 6px;
  margin-bottom: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.search-field {
  max-width: 400px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
}

/* Mobile View Styles */
.mobile-view .invoice-card {
  border-radius: 12px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.mobile-view .invoice-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.invoice-number {
  font-weight: 700;
  font-size: 16px;
}

.status-chip {
  font-weight: 600;
}

.invoice-detail {
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 8px;
}

.detail-label {
  font-weight: 600;
  color: #666;
  min-width: 80px;
}

.detail-value {
  color: #212121;
}

.case-title {
  color: #757575;
  font-style: italic;
}

.text-error {
  color: #d32f2f;
  font-weight: 600;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #757575;
}

/* Desktop View Styles */
.desktop-view {
  border-radius: 12px;
  overflow: hidden;
}

.modern-table {
  background: white;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.client-info .client-name {
  font-weight: 600;
  color: #212121;
}

.client-info .client-case {
  font-size: 12px;
  color: #757575;
  font-style: italic;
}

.amount-text {
  font-weight: 600;
  color: #1976d2;
}

/* Dialog Styles */
.mark-paid-card {
  border-radius: 12px;
}

.dialog-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 700;
}

.invoice-payment-card {
  border-radius: 8px;
  transition: all 0.2s;
}

.invoice-payment-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.invoice-info {
  line-height: 1.6;
}

.payment-summary {
  line-height: 1.8;
}

.quick-payment-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.summary-label {
  font-weight: 600;
  color: #424242;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #1976d2;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    align-items: stretch;
  }

  .search-field {
    max-width: 100%;
  }

  .action-buttons {
    justify-content: stretch;
  }

  .action-btn {
    flex: 1;
  }

  .page-title {
    font-size: 24px;
  }
}

@media (max-width: 600px) {
  .invoices-container {
    padding: 4px;
  }

  .header-card {
    padding: 16px;
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 20px;
  }

  .invoice-detail {
    flex-direction: column;
    align-items: flex-start;
    padding: 6px 0;
  }

  .detail-label {
    min-width: auto;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invoice-card,
.invoice-payment-card {
  animation: fadeIn 0.3s ease-out;
}

.bg-light-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #f0f0f0;
}

.border-grey {
  border: 1px solid #eeeeee !important;
}

.border-top-dashed {
  border-top: 1px dashed #e0e0e0;
}

.bg-red-lighten {
  background-color: #ffebee;
}

.line-height-tight {
  line-height: 1.25;
}
</style>
